import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, message, Empty, Popconfirm, Tag, Tooltip, Switch, Pagination } from 'antd';
import { DeleteOutlined, ReloadOutlined, FilePdfOutlined, InboxOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { makeGetCall, makePostCall, makeDeleteCall, getUserDetails, hasPermission, formatAgeShort, STUDY_DATE_FORMAT } from '../../utils/helper';
import { launchViewerOnDualMonitor } from '../../utils/MonitorManager';
import FolderTree, { FolderTreeNode } from './components/FolderTree';
import ReportDialog from './components/ReportDialog';
import { useUserCenters, useCenterPicker } from './components/CenterPicker';
import './academics.css';

type LibraryFolder = {
  lf_id: number;
  lf_parent_id: number | null;
  lf_name: string;
  lf_visibility: 'private' | 'public';
  lf_owner_username: string | null;
  lf_center_code?: string | null;
};

type LibraryBookmark = {
  lb_id: number;
  lb_folder_id: number;
  lb_study_uid: string;
  lb_accession_no: string | null;
  lb_modality: string | null;
  lb_body_part: string | null;
  lb_study_date: string | null;
  lb_patient_name: string | null;
  lb_patient_age: string | null;
  lb_patient_sex: string | null;
  lb_pacs_ord_id: number | null;
  lb_remarks: string | null;
  lb_bookmarked_by: string;
  lb_bookmarked_at: string;
  lb_has_report?: boolean;
};

type Props = {
  StyledTabsComponent?: () => React.ReactNode;
  // When rendered inside a dialog (profile-menu modal), fill the modal body
  // instead of the viewport, and drop the page-level card chrome/padding.
  inDialog?: boolean;
};

const toTreeNodes = (folders: LibraryFolder[]): FolderTreeNode[] =>
  folders.map(f => ({ id: f.lf_id, parentId: f.lf_parent_id, name: f.lf_name }));

const AcademicsPage: React.FC<Props> = ({ StyledTabsComponent, inDialog = false }) => {
  const [folders, setFolders] = useState<LibraryFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [bookmarks, setBookmarks] = useState<LibraryBookmark[]>([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [anonymize, setAnonymize] = useState(false);
  const [reportDialogBookmarkId, setReportDialogBookmarkId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [total, setTotal] = useState(0);

  const userCenters = useUserCenters();
  const { pickCenter, modal: centerPickerModal } = useCenterPicker(userCenters);
  const centerLabelByCode = useMemo(
    () => new Map(userCenters.map(c => [c.value, c.label])),
    [userCenters]
  );

  const publicFolders = useMemo(
    () => folders.filter(f => f.lf_visibility === 'public'),
    [folders]
  );
  const privateFolders = useMemo(
    () => folders.filter(f => f.lf_visibility === 'private'),
    [folders]
  );
  // For multi-center users, suffix each public folder with its center so the
  // two centers' public trees are distinguishable.
  const publicTreeNodes = useMemo<FolderTreeNode[]>(() => {
    const showCenter = userCenters.length > 1;
    return publicFolders.map(f => {
      const label = f.lf_center_code ? centerLabelByCode.get(f.lf_center_code) : null;
      const name = showCenter && f.lf_parent_id == null && label
        ? `${f.lf_name} (${label})`
        : f.lf_name;
      return { id: f.lf_id, parentId: f.lf_parent_id, name };
    });
  }, [publicFolders, userCenters, centerLabelByCode]);
  const privateTreeNodes = useMemo(() => toTreeNodes(privateFolders), [privateFolders]);

  const loadFolders = async () => {
    setFoldersLoading(true);
    try {
      const res = await makeGetCall('/library/folders');
      setFolders(res?.data?.data || []);
    } catch (err) {
      console.error('Failed to load folders', err);
      message.error('Failed to load folders');
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  };

  const loadBookmarks = async (folderId: number, page: number, pageSize: number) => {
    setBookmarksLoading(true);
    try {
      const res = await makeGetCall(
        `/library/bookmarks?folder_id=${folderId}&page=${page}&page_size=${pageSize}`
      );
      setBookmarks(res?.data?.data || []);
      setTotal(res?.data?.total ?? 0);
    } catch (err) {
      console.error('Failed to load bookmarks', err);
      message.error('Failed to load bookmarks');
      setBookmarks([]);
      setTotal(0);
    } finally {
      setBookmarksLoading(false);
    }
  };

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => {
    setPagination(p => ({ ...p, current: 1 }));
    if (selectedFolderId) {
      loadBookmarks(selectedFolderId, 1, pagination.pageSize);
    } else {
      setBookmarks([]);
      setTotal(0);
    }
  }, [selectedFolderId]);

  const handleCreateFolder = async (
    parentId: number | null,
    name: string,
    visibility: 'public' | 'private'
  ) => {
    // Root public folders must be tagged to a center. Sub-folders inherit the
    // parent's center on the backend, so no prompt there.
    let center_code: string | null | undefined;
    if (visibility === 'public' && parentId == null) {
      try {
        center_code = await pickCenter();
      } catch {
        return; // user cancelled center selection — abort create
      }
    }

    try {
      const res = await makePostCall('/library/folders', { name, parent_id: parentId, visibility, center_code });
      const created: LibraryFolder = res?.data?.data;
      await loadFolders();
      if (created) setSelectedFolderId(created.lf_id);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create folder';
      message.error(msg);
      throw err;
    }
  };

  const handleDeleteFolder = async (folderId: number) => {
    try {
      await makeDeleteCall(`/library/folders/${folderId}`);
      message.success('Folder deleted');
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      await loadFolders();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete folder';
      message.error(msg);
    }
  };

  const handleDeleteBookmark = async (bookmarkId: number) => {
    try {
      await makeDeleteCall(`/library/bookmarks/${bookmarkId}`);
      message.success('Bookmark removed');
      if (selectedFolderId) {
        // If we just deleted the last row on this page, step back one page (unless we're on page 1).
        const remainingOnPage = bookmarks.length - 1;
        const nextPage = remainingOnPage === 0 && pagination.current > 1
          ? pagination.current - 1
          : pagination.current;
        if (nextPage !== pagination.current) {
          setPagination(p => ({ ...p, current: nextPage }));
        }
        loadBookmarks(selectedFolderId, nextPage, pagination.pageSize);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete bookmark';
      message.error(msg);
    }
  };

  const openViewer = async (bookmark: LibraryBookmark) => {
    if (!bookmark.lb_study_uid) {
      message.warning('No study UID on this bookmark');
      return;
    }
    const orderShim = {
      pacs_order: {
        po_modality: bookmark.lb_modality || undefined,
        po_body_part: bookmark.lb_body_part || undefined
      }
    };
    const win = await launchViewerOnDualMonitor(bookmark.lb_study_uid, orderShim);
    if (!win) message.warning('Could not open viewer window');
  };

  const formatStudyDate = (raw: string | null) => {
    if (!raw) return '-';
    const m = moment(raw);
    return m.isValid() ? m.format(STUDY_DATE_FORMAT) : raw;
  };

  const ageSex = (b: LibraryBookmark) => {
    const age = formatAgeShort(null, b.lb_patient_age) || '';
    const sex = b.lb_patient_sex || '';
    if (!age && !sex) return '-';
    return `${age}${age && sex ? ' / ' : ''}${sex}`;
  };

  const currentUserDetails = getUserDetails();
  const currentUsername = currentUserDetails?.username;
  const canManagePublicFolders =
    currentUserDetails?.user_type === 'admin' || hasPermission('MANAGE_LIB_FOLDERS');
  const selectedFolder = folders.find(f => f.lf_id === selectedFolderId);
  const isPublicFolder = selectedFolder?.lf_visibility === 'public';

  const baseColumns: ColumnsType<LibraryBookmark> = [
    {
      title: 'Modality',
      dataIndex: 'lb_modality',
      key: 'modality',
      width: 90,
      render: v => v ? <Tag color="blue">{v}</Tag> : '-'
    },
    {
      title: 'Patient Name',
      dataIndex: 'lb_patient_name',
      key: 'patient_name',
      width: 200,
      ellipsis: true,
      render: v => {
        if (anonymize) return <span style={{ color: '#999' }}>***</span>;
        return v || '-';
      }
    },
    {
      title: 'Description',
      dataIndex: 'lb_body_part',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: v => v || '-'
    },
    {
      title: 'Age / Sex',
      key: 'age_sex',
      width: 110,
      render: (_, rec) => ageSex(rec)
    },
    {
      title: 'Study Date',
      dataIndex: 'lb_study_date',
      key: 'study_date',
      width: 130,
      render: (v: string | null) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatStudyDate(v)}</span>
      )
    },
    {
      title: 'Remarks',
      dataIndex: 'lb_remarks',
      key: 'remarks',
      width: 280,
      ellipsis: { showTitle: false },
      render: v => v
        ? <Tooltip title={v} placement="topLeft"><span>{v}</span></Tooltip>
        : <span style={{ color: '#999' }}>—</span>
    },
    {
      title: 'Report',
      key: 'report',
      width: 80,
      align: 'center',
      fixed: 'right',
      render: (_, rec) => {
        const has = !!rec.lb_has_report;
        return (
          <Tooltip title={has ? 'View report' : 'No signed report available'}>
            <Button
              type="text"
              shape="circle"
              icon={<FilePdfOutlined style={{ fontSize: 20 }} />}
              disabled={!has}
              style={{ color: has ? '#1677ff' : undefined }}
              onClick={(e) => {
                e.stopPropagation();
                if (has) setReportDialogBookmarkId(rec.lb_id);
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'center',
      fixed: 'right',
      render: (_, rec) => {
        const canDelete =
          !isPublicFolder ||
          rec.lb_bookmarked_by === currentUsername ||
          canManagePublicFolders;
        const button = (
          <Button
            type="text"
            danger
            shape="circle"
            disabled={!canDelete}
            icon={<DeleteOutlined style={{ fontSize: 20 }} />}
            onClick={(e) => e.stopPropagation()}
          />
        );
        if (!canDelete) {
          return (
            <Tooltip title="Only the user who bookmarked this case can remove it">
              {button}
            </Tooltip>
          );
        }
        return (
          <Popconfirm
            title="Remove this bookmark?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={(e) => { e?.stopPropagation?.(); handleDeleteBookmark(rec.lb_id); }}
            onCancel={(e) => e?.stopPropagation?.()}
          >
            {button}
          </Popconfirm>
        );
      }
    }
  ];

  const columns: ColumnsType<LibraryBookmark> = isPublicFolder
    ? [
        ...baseColumns.slice(0, baseColumns.length - 2),
        {
          title: 'Bookmarked by',
          dataIndex: 'lb_bookmarked_by',
          key: 'bookmarked_by',
          width: 140,
          ellipsis: true,
          render: (v: string) => v || <span style={{ color: '#999' }}>—</span>
        },
        ...baseColumns.slice(baseColumns.length - 2)
      ]
    : baseColumns;

  return (
    <div style={inDialog ? { height: '100%' } : undefined}>
      {StyledTabsComponent && <div>{StyledTabsComponent()}</div>}

      <div style={inDialog ? { padding: 0, height: '100%' } : { padding: '1rem 1.25rem' }}>
        <div style={{
          background: '#FFFFFF',
          borderRadius: inDialog ? 0 : '0.75rem',
          border: inDialog ? 'none' : '1px solid #E2E8F1',
          display: 'flex',
          ...(inDialog
            ? { height: '100%' }
            : { minHeight: 'calc(100vh - 6.5rem)' })
        }}>
          {/* Folder trees (left) */}
          <div style={{ width: '25%', minWidth: 240, borderRight: '1px solid #E2E8F1', padding: '1rem', overflowY: 'auto' }}>
            <FolderTree
              folders={publicTreeNodes}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              onCreate={(parentId, name) => handleCreateFolder(parentId, name, 'public')}
              onDelete={handleDeleteFolder}
              showDelete
              canMutate={canManagePublicFolders}
              loading={foldersLoading}
              title="Public"
              emptyText="No public folders yet"
            />
            <div style={{ height: 18 }} />
            <FolderTree
              folders={privateTreeNodes}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              onCreate={(parentId, name) => handleCreateFolder(parentId, name, 'private')}
              onDelete={handleDeleteFolder}
              showDelete
              loading={foldersLoading}
              title="My Library"
              emptyText="No folders yet"
            />
          </div>

          {/* Bookmark table (right) */}
          <div style={{ flex: 1, minWidth: 0, padding: '1rem', display: 'flex', flexDirection: 'column' }}>
            {(() => {
              if (!selectedFolderId) {
                return (
                  <Empty
                    description="Select a folder to view bookmarks"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ marginTop: 'auto', marginBottom: 'auto' }}
                  />
                );
              }

              const hasContent = bookmarksLoading || total > 0;

              if (!hasContent) {
                return (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <strong>{selectedFolder?.lf_name}</strong>
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 1rem' }}>
                      <InboxOutlined style={{ fontSize: 72, color: '#CBD5E1', marginBottom: 16 }} />
                      <div style={{ fontSize: 16, fontWeight: 500, color: '#475569', marginBottom: 6 }}>
                        No studies in this folder yet
                      </div>
                      <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', maxWidth: 360 }}>
                        Go to the PACS tab, select studies, and click <strong>Add to Library</strong> to bookmark them here.
                      </div>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => loadBookmarks(selectedFolderId, pagination.current, pagination.pageSize)}
                        style={{ marginTop: 20 }}
                      >
                        Refresh
                      </Button>
                    </div>
                  </>
                );
              }

              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
                    <div>
                      <strong>{selectedFolder?.lf_name}</strong>
                      <span style={{ marginLeft: 8, color: '#666' }}>
                        ({total} {total === 1 ? 'bookmark' : 'bookmarks'})
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <Tooltip title="Hide patient name in this table">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}>
                          Anonymize
                          <Switch size="small" checked={anonymize} onChange={setAnonymize} />
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => loadBookmarks(selectedFolderId, pagination.current, pagination.pageSize)}
                      >
                        Refresh
                      </Button>
                    </div>
                  </div>

                  <Table
                    rowKey="lb_id"
                    dataSource={bookmarks}
                    columns={columns}
                    loading={bookmarksLoading}
                    size="middle"
                    pagination={false}
                    scroll={{ x: isPublicFolder ? 1290 : 1150, y: inDialog ? 'calc(80vh - 13rem)' : 'calc(100vh - 22rem)' }}
                    onRow={(record) => ({
                      onClick: () => openViewer(record),
                      style: { cursor: 'pointer' }
                    })}
                  />

                  <div className="academics-pagination">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={total}
                      showSizeChanger
                      pageSizeOptions={['10', '20', '50', '100']}
                      onChange={(page, pageSize) => {
                        setPagination({ current: page, pageSize });
                        loadBookmarks(selectedFolderId, page, pageSize);
                      }}
                      showTotal={(t, range) => `Showing ${range[0]}-${range[1]} of ${t} items`}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <ReportDialog
        open={reportDialogBookmarkId != null}
        bookmarkId={reportDialogBookmarkId}
        onClose={() => setReportDialogBookmarkId(null)}
      />

      {centerPickerModal}
    </div>
  );
};

export default AcademicsPage;
